import {
  Col,
  Edit,
  Form,
  FormProps,
  Input,
  ListButton,
  Row,
  Select,
  Switch
} from '@pankod/refine-antd';
import { usePermissions } from '@pankod/refine-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useOrganizationOptions } from 'hooks/useOrganizationOptions';
import { httpClient } from 'services/httpClient';
import { buildAssignableRoleOptions } from 'utils/buildAssignableRoleOptions';
import { buildPersonTitleOptions } from 'utils/buildPersonTitleOptions';

export const UserEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: role } = usePermissions();
  const personTitleOptions = useMemo(() => buildPersonTitleOptions(t), [t]);
  const assignableRoleOptions = useMemo(() => {
    if (!role) return [];
    return buildAssignableRoleOptions(t, role);
  }, [t, role]);

  const loadedUserIdRef = useRef<string | undefined>(undefined);
  const originalEmailRef = useRef<string | undefined>(undefined);

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const formProps: FormProps = { form };
  const saveButtonProps = { loading: saving, onClick: () => form.submit() };

  const handleFinish = async (values: any) => {
    setSaving(true);
    const normalizeEmptyToNull = (value: unknown) => {
      if (typeof value !== 'string') return value;
      return value.trim() === '' ? null : value;
    };

    const payload: Record<string, unknown> = {
      ...values,
      skype: normalizeEmptyToNull((values as any).skype),
      mobile: normalizeEmptyToNull((values as any).mobile),
      work_phone: normalizeEmptyToNull((values as any).work_phone)
    };

    if ((values as any).email === originalEmailRef.current) {
      delete payload.email;
    }

    try {
      if (!id) {
        throw new Error('Missing user id');
      }

      await httpClient.patch(`/admin/users/${id}`, payload);
      window.location.href = '/users';
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        t('notifications.error', { statusCode: 500 });

      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  const { selectProps: organizationSelectProps } = useOrganizationOptions();

  useEffect(() => {
    let mounted = true;

    async function loadUser() {
      if (!id || !formProps.form) return;
      if (loadedUserIdRef.current === id) return;
      loadedUserIdRef.current = id;

      const response = await httpClient.get(`/admin/users/${id}`);
      const user = response.data?.data || response.data;
      if (!mounted) return;

      const organizationId = user?.organization?.id;
      originalEmailRef.current = user.email;

      formProps.form.setFieldsValue({
        orgId: organizationId,
        role: user.role,
        title: user.title || personTitleOptions[0]?.value,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        skype: (user as any).skype,
        mobileNumber: (user as any).mobileNumber,
        workPhone: (user as any).workPhone,
        isActive: (user as any).isActive ?? true
      });
    }

    loadUser().catch(() => {});

    return () => {
      mounted = false;
    };
  }, [id, formProps.form, personTitleOptions]);

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      pageHeaderProps={{ extra: <ListButton /> }}
    >
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Row gutter={[40, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.organization.label')}
              name="orgId"
              rules={[
                {
                  required: true,
                  message: t(
                    'pages.userForm.fields.organization.errors.required'
                  )
                }
              ]}
            >
              <Select
                {...organizationSelectProps}
                placeholder={t(
                  'pages.userForm.fields.organization.placeholder'
                )}
              />
            </Form.Item>
          </Col>
          {!!assignableRoleOptions?.length && (
            <Col span={12}>
              <Form.Item
                label={t('pages.userForm.fields.role.label')}
                name="role"
                rules={[
                  {
                    required: true,
                    message: t('pages.userForm.fields.role.errors.required')
                  }
                ]}
              >
                <Select
                  options={assignableRoleOptions}
                  placeholder={t('pages.userForm.fields.role.placeholder')}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.firstName.label')}
              name="firstName"
              rules={[
                {
                  required: true,
                  message: t('pages.userForm.fields.firstName.errors.required')
                }
              ]}
            >
              <Input
                addonBefore={
                  <Form.Item name="title">
                    <Select
                      placeholder={t('pages.userForm.fields.title.placeholder')}
                      options={personTitleOptions}
                      style={{ width: '120px' }}
                    />
                  </Form.Item>
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.lastName.label')}
              name="lastName"
              rules={[
                {
                  required: true,
                  message: t('pages.userForm.fields.lastName.errors.required')
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.email.label')}
              name="email"
              rules={[
                {
                  required: true,
                  message: t('pages.userForm.fields.email.errors.required')
                },
                {
                  type: 'email',
                  message: t('pages.userForm.fields.email.errors.invalid')
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.password.label')}
              name="password"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={8}>
            <Form.Item
              label={t('pages.userForm.fields.skype.label')}
              name="skype"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('pages.userForm.fields.mobile.label')}
              name="mobileNumber"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('pages.userForm.fields.workPhone.label')}
              name="workPhone"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch defaultChecked />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
